import { Button, ModalFooter } from '@execonline-inc/execonline-ui';
import { assertNever } from '@kofno/piper';
import { ReviewEntry } from '../Stores/ShareStore/Types';
import { T } from '../../../../../Translations';

interface Props {
  refreshing: boolean;
  invitationCount: number;
  // False when the request would carry nobody at all, which is the only reason
  // to withhold the send action.
  canSend: boolean;
  // Without a scheduled time no email is dispatched at all, so the primary
  // action must not promise one.
  willSendEmail: boolean;
  // Opened from the banner's "View details" the review only informs; Send
  // belongs to the Confirm step (DVLP-8042).
  reviewEntry: ReviewEntry;
  onCancel: () => void;
  onRefresh: () => void;
  onSend: () => void;
}

function Footer({
  refreshing,
  invitationCount,
  canSend,
  willSendEmail,
  reviewEntry,
  onCancel,
  onRefresh,
  onSend,
}: Props) {
  // A group whose members hold no license is still invited, so the count of
  // licensed people can be zero while there is genuinely something to send.
  const emailLabel =
    invitationCount === 0 ? (
      <T kind="Send invitations" />
    ) : invitationCount === 1 ? (
      <T kind="Send invitation" />
    ) : (
      <T kind="Send {{sending}} invitations" sending={invitationCount} />
    );

  // Nothing is emailed without a scheduled time; the participants simply gain
  // access. Saying "Send" there describes something that will not happen.
  const sendLabel = willSendEmail ? emailLabel : <T kind="Add to collection" />;

  // From Confirm the dialog stands between the admin and sending, so leaving it
  // is a cancel. From the banner nothing is being cancelled; it closes.
  const offersSend = (entry: ReviewEntry): boolean => {
    switch (entry) {
      case 'confirm':
        return true;
      case 'license-alert':
        return false;
      default:
        return assertNever(entry);
    }
  };

  return (
    <ModalFooter
      className="flex-row flex-wrap items-center justify-end gap-2"
      data-intercom-target="share-license-alerts-footer"
    >
      <Button
        variant="text"
        className="text-muted-foreground hover:bg-muted"
        onClick={onCancel}
        data-intercom-target="share-license-alert-cancel"
      >
        {offersSend(reviewEntry) ? <T kind="Cancel" /> : <T kind="Close" />}
      </Button>

      <Button
        variant="outlined"
        disabled={refreshing}
        isLoading={refreshing}
        onClick={onRefresh}
        data-intercom-target="share-license-alert-refresh"
      >
        <T kind="Recheck licenses" />
      </Button>

      {/* Nobody to invite means no action to offer. Recheck is the way forward
          once licenses have been assigned on the People screen. */}
      {canSend && offersSend(reviewEntry) && (
        <Button
          className="border-accent bg-accent text-accent-foreground hover:border-accent/90 hover:bg-accent/90"
          disabled={refreshing}
          onClick={onSend}
          data-intercom-target="share-license-alert-primary"
        >
          {sendLabel}
        </Button>
      )}
    </ModalFooter>
  );
}

export default Footer;
